import { useState } from "react";
import type {
  Blueprint,
  GuidedAnswers,
  GuidedFollowUpAnswers,
  GuidedFollowUpId,
  GuidedQuestionId,
  GuidedUnsureFlags,
} from "@/features/blueprints/blueprintTypes";
import {
  getFollowUpById,
  getTriggeredFollowUps,
  pruneSmartFollowUpAnswers,
} from "./guidedFollowUps";
import { guidedQuestions, getGuidedQuestionIndex } from "./guidedQuestions";
import { GuidedFollowUpStep } from "./GuidedFollowUpStep";
import { GuidedQuestionStep } from "./GuidedQuestionStep";
import { GuidedReview } from "./GuidedReview";
import {
  GUIDED_TEXT_MAX_LENGTH,
  type GuidedSaveState,
  type GuidedStep,
} from "./guidedTypes";

type GuidedModeFlowProps = {
  blueprint: Blueprint;
  saveState: GuidedSaveState;
  isGenerating: boolean;
  generationMessage?: string;
  error: string | null;
  onSave: (
    answers: GuidedAnswers,
    smartFollowupAnswers: GuidedFollowUpAnswers,
    unsureFlags: GuidedUnsureFlags,
  ) => Promise<void>;
  onBackToFastMode: () => void;
  onGenerate: () => void;
  onSaveAndFinishLater: () => void;
};

function initialAnswers(blueprint: Blueprint): GuidedAnswers {
  return {
    ...(blueprint.guided_answers ?? {}),
    q1: {
      projectName:
        blueprint.guided_answers?.q1?.projectName ??
        (blueprint.title.endsWith(" draft") ? "" : blueprint.title),
      appIdea: blueprint.guided_answers?.q1?.appIdea ?? blueprint.input_text,
    },
  };
}

function initialStep(answers: GuidedAnswers): GuidedStep {
  if (!answers.q1?.appIdea?.trim()) {
    return "q1";
  }

  if (!answers.q2 && !answers.q3 && !answers.q4 && !answers.q5) {
    return "q2";
  }

  if (answers.q1?.appIdea && answers.q2 && answers.q3 && answers.q4 && answers.q5) {
    return "review";
  }

  const firstIncomplete = guidedQuestions.find((question) => {
    if (question.id === "q1") {
      return !answers.q1?.appIdea?.trim();
    }

    return !answers[question.id];
  });

  return firstIncomplete?.id ?? "review";
}

function hasQuestionAnswer(
  questionId: GuidedQuestionId,
  answers: GuidedAnswers,
  unsureFlags: GuidedUnsureFlags,
): boolean {
  if (questionId === "q1") {
    return Boolean(answers.q1?.appIdea?.trim());
  }

  if (unsureFlags[questionId]) {
    return true;
  }

  if (questionId === "q2") {
    return Boolean(
      answers.q2?.text?.trim() || (answers.q2?.chips?.length ?? 0) > 0,
    );
  }

  if (questionId === "q3") {
    return Boolean(
      answers.q3?.extraText?.trim() || (answers.q3?.chips?.length ?? 0) > 0,
    );
  }

  if (questionId === "q4") {
    return Boolean((answers.q4?.chips?.length ?? 0) > 0);
  }

  if (questionId === "q5") {
    return Boolean(
      answers.q5?.extraText?.trim() || (answers.q5?.chips?.length ?? 0) > 0,
    );
  }

  return false;
}

export function GuidedModeFlow({
  blueprint,
  saveState,
  isGenerating,
  generationMessage,
  error,
  onSave,
  onBackToFastMode,
  onGenerate,
  onSaveAndFinishLater,
}: GuidedModeFlowProps) {
  const [answers, setAnswers] = useState<GuidedAnswers>(() =>
    initialAnswers(blueprint),
  );
  const [unsureFlags, setUnsureFlags] = useState<GuidedUnsureFlags>(
    blueprint.unsure_flags ?? {},
  );
  const [smartFollowupAnswers, setSmartFollowupAnswers] =
    useState<GuidedFollowUpAnswers>(blueprint.smart_followup_answers ?? {});
  const [step, setStep] = useState<GuidedStep>(() =>
    initialStep(initialAnswers(blueprint)),
  );

  async function saveAndMove(nextStep: GuidedStep) {
    const activeFollowUps = getTriggeredFollowUps(answers);
    const prunedFollowupAnswers = pruneSmartFollowUpAnswers(
      smartFollowupAnswers,
      activeFollowUps,
    );

    setSmartFollowupAnswers(prunedFollowupAnswers);
    await onSave(answers, prunedFollowupAnswers, unsureFlags);
    setStep(nextStep);
  }

  async function handleBack() {
    if (step === "q1") {
      await onSave(answers, smartFollowupAnswers, unsureFlags);
      onBackToFastMode();
      return;
    }

    if (step === "extra") {
      const activeFollowUps = getTriggeredFollowUps(answers);
      const previousFollowUp = activeFollowUps.at(-1);

      await saveAndMove(previousFollowUp ? `followup:${previousFollowUp.id}` : "q5");
      return;
    }

    if (step === "review") {
      await saveAndMove("extra");
      return;
    }

    const currentIndex = getGuidedQuestionIndex(step);
    await saveAndMove(guidedQuestions[Math.max(0, currentIndex - 1)].id);
  }

  async function handleNext(questionId: GuidedQuestionId) {
    const currentIndex = getGuidedQuestionIndex(questionId);
    const nextQuestion = guidedQuestions[currentIndex + 1];

    if (nextQuestion) {
      await saveAndMove(nextQuestion.id);
      return;
    }

    const activeFollowUps = getTriggeredFollowUps(answers);
    await saveAndMove(
      activeFollowUps.length > 0 ? `followup:${activeFollowUps[0].id}` : "extra",
    );
  }

  async function handleFollowUpBack(followUpId: GuidedFollowUpId) {
    const activeFollowUps = getTriggeredFollowUps(answers);
    const currentIndex = activeFollowUps.findIndex(
      (followUp) => followUp.id === followUpId,
    );
    const previousFollowUp = activeFollowUps[currentIndex - 1];

    await saveAndMove(previousFollowUp ? `followup:${previousFollowUp.id}` : "q5");
  }

  async function handleFollowUpNext(followUpId: GuidedFollowUpId) {
    const activeFollowUps = getTriggeredFollowUps(answers);
    const currentIndex = activeFollowUps.findIndex(
      (followUp) => followUp.id === followUpId,
    );
    const nextFollowUp = activeFollowUps[currentIndex + 1];

    await saveAndMove(nextFollowUp ? `followup:${nextFollowUp.id}` : "extra");
  }

  async function handleFollowUpSkip(followUpId: GuidedFollowUpId) {
    const activeFollowUps = getTriggeredFollowUps(answers);
    const nextSmartFollowupAnswers = {
      ...smartFollowupAnswers,
      [followUpId]: {
        skipped: true,
        unsure: false,
        answer: "",
      },
    };
    const prunedFollowupAnswers = pruneSmartFollowUpAnswers(
      nextSmartFollowupAnswers,
      activeFollowUps,
    );
    const currentIndex = activeFollowUps.findIndex(
      (followUp) => followUp.id === followUpId,
    );
    const nextFollowUp = activeFollowUps[currentIndex + 1];

    setSmartFollowupAnswers(prunedFollowupAnswers);
    await onSave(answers, prunedFollowupAnswers, unsureFlags);
    setStep(nextFollowUp ? `followup:${nextFollowUp.id}` : "extra");
  }

  function updateFollowUpAnswer(
    followUpId: GuidedFollowUpId,
    answer: NonNullable<GuidedFollowUpAnswers[GuidedFollowUpId]>,
  ) {
    setSmartFollowupAnswers({
      ...smartFollowupAnswers,
      [followUpId]: answer,
    });
  }

  if (step === "extra") {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-indigo-700">Guided Mode</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
              Anything else we should know?
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
              This is optional. Add anything important that did not fit into the
              earlier questions.
            </p>
          </div>
          <span className="rounded-md bg-slate-100 px-3 py-1 text-sm font-semibold capitalize text-slate-700">
            {saveState === "idle" ? "Loaded" : saveState}
          </span>
        </div>
        <textarea
          value={answers.extraDetail ?? ""}
          onChange={(event) =>
            setAnswers({ ...answers, extraDetail: event.target.value })
          }
          maxLength={GUIDED_TEXT_MAX_LENGTH}
          rows={5}
          className="mt-6 w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm leading-6 text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
        <p className="mt-2 text-xs text-slate-500">
          {(answers.extraDetail ?? "").length} / {GUIDED_TEXT_MAX_LENGTH}
        </p>
        <div className="mt-6 flex flex-wrap justify-between gap-2">
          <button
            type="button"
            onClick={() => void handleBack()}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            Back
          </button>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void saveAndMove("review")}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Skip
            </button>
            <button
              type="button"
              onClick={() => void saveAndMove("review")}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
            >
              Review My Blueprint Brief
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (step === "review") {
    return (
      <GuidedReview
        answers={answers}
        smartFollowupAnswers={smartFollowupAnswers}
        unsureFlags={unsureFlags}
        saveState={saveState}
        isGenerating={isGenerating}
        generationMessage={generationMessage}
        error={error}
        onEdit={setStep}
        onGenerate={onGenerate}
        onSaveAndFinishLater={() => {
          void onSave(answers, smartFollowupAnswers, unsureFlags).then(
            onSaveAndFinishLater,
          );
        }}
      />
    );
  }

  if (step.startsWith("followup:")) {
    const followUpId = step.replace("followup:", "") as GuidedFollowUpId;
    const followUp = getFollowUpById(followUpId);
    const activeFollowUps = getTriggeredFollowUps(answers);
    const followUpIndex = activeFollowUps.findIndex(
      (item) => item.id === followUpId,
    );

    if (!followUp || followUpIndex === -1) {
      return (
        <section className="rounded-lg border border-slate-200 bg-white p-6">
          <p className="text-sm text-slate-600">
            This clarification is no longer needed.
          </p>
          <button
            type="button"
            onClick={() => void saveAndMove("extra")}
            className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
          >
            Continue
          </button>
        </section>
      );
    }

    return (
      <GuidedFollowUpStep
        followUp={followUp}
        followUpNumber={followUpIndex + 1}
        totalFollowUps={activeFollowUps.length}
        answer={smartFollowupAnswers[followUpId]}
        saveLabel={saveState === "idle" ? "Loaded" : saveState}
        onAnswerChange={(answer) => updateFollowUpAnswer(followUpId, answer)}
        onBack={() => void handleFollowUpBack(followUpId)}
        onNext={() => void handleFollowUpNext(followUpId)}
        onSkip={() => void handleFollowUpSkip(followUpId)}
      />
    );
  }

  const question = guidedQuestions.find((item) => item.id === step);

  if (!question) {
    return null;
  }

  return (
    <GuidedQuestionStep
      question={question}
      questionNumber={getGuidedQuestionIndex(question.id) + 1}
      totalQuestions={guidedQuestions.length}
      answers={answers}
      unsureFlags={unsureFlags}
      onAnswersChange={setAnswers}
      onUnsureFlagsChange={setUnsureFlags}
      onBack={() => void handleBack()}
      onNext={() => void handleNext(question.id)}
      canGoNext={hasQuestionAnswer(question.id, answers, unsureFlags)}
      saveLabel={saveState === "idle" ? "Loaded" : saveState}
    />
  );
}
